/**
 * FloatingOverlay — a portal-based, anchor-aware overlay primitive.
 *
 * Key properties:
 *  - Renders at document.body via createPortal (no z-index / overflow clipping issues)
 *  - Two-pass positioning: renders invisible first, measures own size, then positions
 *  - Viewport-aware: flips vertically and clamps horizontally to stay on screen
 *  - Reacts to window resize and scroll
 *  - Closes on outside click (excluding the anchor) and Escape key
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OverlayPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface FloatingOverlayProps {
  open: boolean;
  onClose: () => void;
  /** Element the overlay is anchored to. Omit for centered/modal mode. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Preferred placement relative to the anchor. Default: 'bottom'. */
  preferredPlacement?: OverlayPlacement;
  /** Gap between the anchor edge and the overlay in px. Default: 6. */
  offset?: number;
  /** Minimum distance kept from each viewport edge in px. Default: 12. */
  edgePadding?: number;
  /** Additional class names applied to the overlay container. */
  className?: string;
  children: React.ReactNode;
}

interface ResolvedPosition {
  top: number;
  left: number;
  maxHeight?: number;
}

// ---------------------------------------------------------------------------
// Position computation
// ---------------------------------------------------------------------------

const DEFAULT_OFFSET = 6;
const DEFAULT_EDGE_PADDING = 12;

function resolvePosition(
  anchorRect: DOMRect,
  overlayEl: HTMLElement,
  viewport: { width: number; height: number },
  preferredPlacement: OverlayPlacement,
  offset: number,
  edgePadding: number,
): ResolvedPosition {
  const ow = overlayEl.offsetWidth;
  const oh = overlayEl.offsetHeight;

  const spaceBelow = viewport.height - anchorRect.bottom - offset;
  const spaceAbove = anchorRect.top - offset;

  // --- Vertical axis ---
  // Prefer the requested side; fall back to the side with more room.
  const wantBottom =
    preferredPlacement === 'bottom' ||
    preferredPlacement === 'left' ||
    preferredPlacement === 'right';
  const fitBelow = spaceBelow >= oh;
  const fitAbove = spaceAbove >= oh;

  let top: number;
  let maxHeight: number | undefined;

  const useBottom = wantBottom
    ? fitBelow || spaceBelow >= spaceAbove
    : !fitAbove && spaceBelow >= spaceAbove;

  if (useBottom) {
    top = anchorRect.bottom + offset;
    if (!fitBelow) {
      maxHeight = Math.max(0, spaceBelow - edgePadding);
    }
  } else {
    // Place above
    if (fitAbove) {
      top = anchorRect.top - oh - offset;
    } else {
      // Not enough space above either — constrain to top edge
      maxHeight = Math.max(0, spaceAbove - edgePadding);
      top = edgePadding;
    }
  }

  // --- Horizontal axis ---
  // Start aligned with the anchor's left edge; shift left if it overflows right.
  let left = anchorRect.left;
  const rightEdge = left + ow;
  if (rightEdge > viewport.width - edgePadding) {
    // Align to anchor's right edge instead
    left = anchorRect.right - ow;
  }
  // Hard clamp so it never leaves the viewport
  left = Math.max(edgePadding, Math.min(left, viewport.width - ow - edgePadding));
  top = Math.max(edgePadding, top);

  return { top, left, maxHeight };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FloatingOverlay({
  open,
  onClose,
  anchorRef,
  preferredPlacement = 'bottom',
  offset = DEFAULT_OFFSET,
  edgePadding = DEFAULT_EDGE_PADDING,
  className,
  children,
}: FloatingOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<ResolvedPosition | null>(null);

  // Recompute position from latest measurements.
  const recompute = useCallback(() => {
    if (!anchorRef?.current || !overlayRef.current) return;
    const anchorRect = anchorRef.current.getBoundingClientRect();
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const pos = resolvePosition(
      anchorRect,
      overlayRef.current,
      viewport,
      preferredPlacement,
      offset,
      edgePadding,
    );
    setPosition(pos);
  }, [anchorRef, preferredPlacement, offset, edgePadding]);

  // First-pass measurement: runs synchronously after the invisible render so we
  // get real dimensions before the browser paints the visible result.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    recompute();
  }, [open, recompute]);

  // Keep position current on resize and scroll.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [open, recompute]);

  // Close on outside pointer-down (but not when clicking the anchor itself).
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (overlayRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };
    // Capture phase so we see the event before anything else consumes it.
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [open, onClose, anchorRef]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, onClose]);

  if (!open) return null;

  const hasAnchor = !!anchorRef;
  const isReady = !hasAnchor || position !== null;

  const positionStyle: React.CSSProperties = hasAnchor
    ? {
        position: 'fixed',
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        maxHeight: position?.maxHeight ?? undefined,
        // Hide until position is resolved to avoid a flash at (0, 0).
        visibility: isReady ? 'visible' : 'hidden',
        overflowY: position?.maxHeight !== undefined ? 'auto' : undefined,
      }
    : {
        // Centered / modal mode — no anchor needed.
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };

  return createPortal(
    <div
      ref={overlayRef}
      style={positionStyle}
      className={cn(
        'z-50',
        // Animate in once position is known; skip the animation frame where
        // the element is still invisible to avoid a stutter.
        isReady ? 'animate-in fade-in-0 zoom-in-95 duration-150' : '',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}
